import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PlusIcon,
  CheckIcon,
  TrashIcon,
  XMarkIcon,
  FlagIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';
import { useFamilyStore } from '../../store/familyStore';

interface TodoItem {
  id: string;
  title: string;
  description: string | null;
  status: 'todo' | 'in_progress' | 'done' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to_id: string | null;
  assigned_to_name: string | null;
  assigned_to_avatar_url?: string | null;
  due_date: string | null;
}

interface TodoBoard {
  id: string;
  family_id: string;
  name: string;
  description: string | null;
  color: string;
}

type AssigneeOption = {
  value: string;
  label: string;
  avatar_url: string | null;
};

const priorityColors = {
  low: 'text-surface-400',
  medium: 'text-blue-400',
  high: 'text-amber-400',
  urgent: 'text-red-400',
};

const getInitials = (name?: string | null) => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  const initials = parts.slice(0, 2).map((p) => p[0]).join('');
  return initials.toUpperCase() || '?';
};

const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
const resolveAvatarUrl = (url?: string | null) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${apiBase}${url}`;
};

const AssigneeAvatar = ({ name, avatarUrl }: { name?: string | null; avatarUrl?: string | null }) => {
  const resolved = resolveAvatarUrl(avatarUrl || undefined);
  if (resolved) {
    return (
      <img
        src={resolved}
        alt={name || 'Assignee'}
        className="w-8 h-8 rounded-full object-cover border border-white/10"
      />
    );
  }

  return (
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500/70 to-accent-500/70 flex items-center justify-center text-xs font-semibold text-white">
      {getInitials(name)}
    </div>
  );
};

export default function TodoBoard() {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentFamily, fetchFamily } = useFamilyStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [filter, setFilter] = useState<'all' | 'todo' | 'done'>('all');
  const [assigneePickerFor, setAssigneePickerFor] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<{ title: string; description: string; priority: string; assigned_to_id: string }>({
    defaultValues: {
      priority: 'medium',
      assigned_to_id: '',
    }
  });

  const { data: board } = useQuery({
    queryKey: ['board', boardId],
    queryFn: async () => {
      const response = await api.get(`/todos/boards/${boardId}`);
      return response.data as TodoBoard;
    },
    enabled: !!boardId,
  });

  useEffect(() => {
    if (board?.family_id && (!currentFamily || currentFamily.id !== board.family_id)) {
      fetchFamily(board.family_id).catch(() => {});
    }
  }, [board?.family_id, currentFamily?.id, fetchFamily]);

  const assignees = useMemo<AssigneeOption[]>(() => {
    if (!currentFamily?.members) return [];

    return currentFamily.members
      .filter((member) => !!member.user_id)
      .map((member) => {
        const fullName = `${member.first_name} ${member.last_name || ''}`.trim();
        const label = (member.nickname || fullName || member.username).trim() || member.username;

        return {
          value: member.user_id,
          label,
          avatar_url: member.avatar_url,
        };
      });
  }, [currentFamily]);

  const { data: items, isLoading } = useQuery({
    queryKey: ['todoItems', boardId],
    queryFn: async () => {
      const response = await api.get(`/todos/boards/${boardId}/items`);
      return response.data as TodoItem[];
    },
    enabled: !!boardId,
  });

  const addMutation = useMutation({
    mutationFn: async (data: { title: string; description: string; priority: string; assigned_to_id: string }) => {
      const payload = {
        ...data,
        assigned_to_id: data.assigned_to_id || null,
      };
      const response = await api.post(`/todos/boards/${boardId}/items`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todoItems', boardId] });
      queryClient.invalidateQueries({ queryKey: ['todoBoards'] });
      toast.success('Task added!');
      setShowAddModal(false);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to add task');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await api.patch(`/todos/items/${id}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todoItems', boardId] });
      queryClient.invalidateQueries({ queryKey: ['todoBoards'] });
    },
  });

  const assignMutation = useMutation({
    mutationFn: async ({ id, assigned_to_id }: { id: string; assigned_to_id: string | null }) => {
      await api.patch(`/todos/items/${id}`, { assigned_to_id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todoItems', boardId] });
      queryClient.invalidateQueries({ queryKey: ['todoBoards'] });
      toast.success('Assignee updated');
      setAssigneePickerFor(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update assignee');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/todos/items/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todoItems', boardId] });
      queryClient.invalidateQueries({ queryKey: ['todoBoards'] });
      toast.success('Task deleted');
    },
  });

  const filteredItems = items?.filter((item) => {
    if (filter === 'todo') return item.status !== 'done';
    if (filter === 'done') return item.status === 'done';
    return true;
  });

  const handleAssigneeChange = (itemId: string, value: string) => {
    assignMutation.mutate({ id: itemId, assigned_to_id: value || null });
  };

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/todos')} className="btn-icon">
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{board?.name || 'Loading...'}</h1>
          {board?.description && (
            <p className="text-surface-400">{board.description}</p>
          )}
        </div>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors">
          <PlusIcon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Add Task</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {(['all', 'todo', 'done'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              filter === f
                ? 'bg-primary-500/20 text-primary-300 border border-primary-500/20'
                : 'text-surface-400 hover:text-white hover:bg-white/5'
            }`}
          >
            {f === 'all' ? 'All' : f === 'todo' ? 'To Do' : 'Done'}
          </button>
        ))}
      </div>

      {/* Items */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : filteredItems?.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-16"
        >
          <CheckIcon className="w-16 h-16 mx-auto mb-4 text-surface-500" />
          <h2 className="text-xl font-bold text-white mb-2">
            {filter === 'done' ? 'No completed tasks' : 'No tasks yet'}
          </h2>
          <p className="text-surface-400 mb-6">
            {filter === 'done' ? 'Complete some tasks to see them here' : 'Add your first task to get started'}
          </p>
          {filter !== 'done' && (
            <button onClick={() => setShowAddModal(true)} className="btn-primary inline-flex">
              <PlusIcon className="w-5 h-5" />
              <span>Add Task</span>
            </button>
          )}
        </motion.div>
      ) : (
        <div className="space-y-3">
          {filteredItems?.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className={`card relative overflow-visible flex items-start gap-4 ${item.status === 'done' ? 'opacity-60' : ''} ${assigneePickerFor === item.id ? 'z-50' : 'z-0'}`}
            >
              <button
                onClick={() => toggleMutation.mutate({
                  id: item.id,
                  status: item.status === 'done' ? 'todo' : 'done'
                })}
                className={`mt-1 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                  item.status === 'done'
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'border-surface-500 hover:border-primary-500'
                }`}
              >
                {item.status === 'done' && <CheckIcon className="w-4 h-4" />}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className={`font-medium ${item.status === 'done' ? 'text-surface-400 line-through' : 'text-white'}`}>
                    {item.title}
                  </h3>
                  <FlagIcon className={`w-4 h-4 ${priorityColors[item.priority]}`} />
                </div>
                {item.description && (
                  <p className="text-sm text-surface-400 line-clamp-2">{item.description}</p>
                )}
                <div className="flex items-center gap-3 mt-2 text-xs text-surface-500">
                  <div className="relative">
                    <button
                      onClick={() => setAssigneePickerFor(assigneePickerFor === item.id ? null : item.id)}
                      className="flex items-center gap-2 rounded-full px-2 py-1 hover:bg-white/5 transition-colors"
                      title={item.assigned_to_name || 'Assign to someone'}
                    >
                      <AssigneeAvatar name={item.assigned_to_name} avatarUrl={item.assigned_to_avatar_url} />
                      <div className="text-left leading-tight">
                        <p className="text-[10px] text-surface-500">Assignee</p>
                        <span className="text-sm text-white">
                          {item.assigned_to_name || 'Unassigned'}
                        </span>
                      </div>
                      {!item.assigned_to_name && (
                        <UserPlusIcon className="w-4 h-4 text-surface-500" />
                      )}
                    </button>

                    {assigneePickerFor === item.id && (
                      <div
                        className="absolute right-0 top-full mt-2 w-56 rounded-2xl border border-white/10 bg-surface-900/95 p-3 shadow-2xl z-[9999]"
                      >
                        <label className="input-label text-xs mb-2">Assign to</label>
                        <select
                          className="input text-sm"
                          value={item.assigned_to_id || ''}
                          onChange={(e) => handleAssigneeChange(item.id, e.target.value)}
                          disabled={assignMutation.isPending}
                        >
                          <option value="">Unassigned</option>
                          {assignees.map((member) => (
                            <option key={member.value} value={member.value}>
                              {member.label}
                            </option>
                          ))}
                        </select>
                        {!assignees.length && (
                          <p className="text-xs text-surface-500 mt-2">No family members available</p>
                        )}
                        <div className="flex justify-end mt-2">
                          <button
                            type="button"
                            onClick={() => setAssigneePickerFor(null)}
                            className="text-xs text-surface-400 hover:text-white"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {item.due_date && <span>Due {new Date(item.due_date).toLocaleDateString()}</span>}
                </div>
              </div>

              <button
                onClick={() => deleteMutation.mutate(item.id)}
                className="btn-icon text-surface-500 hover:text-red-400"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      <AnimatePresence>
        {showAddModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowAddModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Add Task</h2>
                <button onClick={() => setShowAddModal(false)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit((data) => addMutation.mutate(data))} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Task Title</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="What needs to be done?"
                    {...register('title', { required: 'Title is required' })}
                  />
                  {errors.title && (
                    <p className="text-sm text-red-400">{errors.title.message}</p>
                  )}
                </div>

                <div className="input-group">
                  <label className="input-label">Description</label>
                  <textarea
                    className="input resize-none"
                    rows={3}
                    placeholder="Add some details..."
                    {...register('description')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Priority</label>
                  <select className="input" {...register('priority')}>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="input-group">
                  <label className="input-label">Assign to</label>
                  <select className="input" {...register('assigned_to_id')}>
                    <option value="">Unassigned</option>
                    {assignees.map((member) => (
                      <option key={member.value} value={member.value}>
                        {member.label}
                      </option>
                    ))}
                  </select>
                  {!assignees.length && (
                    <p className="text-sm text-surface-500 mt-1">No family members to assign yet</p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addMutation.isPending}
                    className="btn-primary flex-1"
                  >
                    {addMutation.isPending ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      'Add Task'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

